import { ensureAccessToken, handleUnauthorized, clearTokens, seedDevTokens } from "./jwt"

const isDev = process.env.NODE_ENV === "development";

// Dev-only fallback bearer, used when window.__USER__ is not set (e.g. webpack
// dev server without a PHP backend). Never hardcode a real token here — it is
// injected at build time from ROBERT_DEV_TOKEN (.env / systemvars). A
// `robert:devToken` localStorage entry overrides it so you can send your own
// OAuth bearer without touching the build. Both are inert in production
// builds (guarded by isDev). If neither is set, dev requests go unauthenticated
// and the caller renders its own error state.
const DEV_TOKEN = process.env.ROBERT_DEV_TOKEN || "";

function devOverrideToken(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return window.localStorage?.getItem("robert:devToken") || undefined
  } catch {
    return undefined
  }
}

let devSeeded = false
function seedFromHarness(): void {
  if (!isDev || devSeeded || typeof window === "undefined") return
  devSeeded = true
  try {
    const access = window.localStorage?.getItem("robert:devToken") || ""
    const refresh = window.localStorage?.getItem("robert:devRefresh")
    // Only a pair is worth seeding into the rotating store; a bare access token
    // keeps working through the existing devOverrideToken() path below.
    if (access && refresh) seedDevTokens(access, refresh)
  } catch { /* storage unavailable */ }
}

/**
 * The currency the page shows prices in: the backend resolved it for this domain and profile
 * (`window.__USER__.currency`). REST works the storefront out from the user's own source
 * company instead, which is not the domain the page is open on (a travel-code.com account on
 * travelhub.by gets EUR for BYN), so a search passes this one as its `currency`, as the flight
 * search always has. A token request never writes it into the profile.
 */
export function pageCurrency(): string | undefined {
  return window.__USER__?.currency || undefined
}

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  seedFromHarness()
  const headers = new Headers(init?.headers);

  // Try JWT first; fall back to static token from Yii2 or DEV_TOKEN
  const token =
    (await ensureAccessToken()) ||
    window.__USER__?.accessToken ||
    (isDev ? (devOverrideToken() || DEV_TOKEN) : undefined);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  // The server answers errors in this language until it knows the user (an
  // expired token is reported before that) — the page's language, not the
  // browser's.
  const uiLang = window.__USER__?.lang
  if (uiLang && !headers.has("Accept-Language")) {
    headers.set("Accept-Language", uiLang);
  }

  let response = await fetch(input, {
    ...init,
    headers,
    ...(!isDev && { credentials: "include" }),
  });

  // 401 — try to recover via JWT (refresh → session cookie)
  if (response.status === 401) {
    const newToken = await handleUnauthorized();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      response = await fetch(input, {
        ...init,
        headers,
        ...(!isDev && { credentials: "include" }),
      });
    } else if (!window.__USER__?.accessToken && !isDev) {
      // No JWT and no static token — session is dead.
      // On the webpack dev server there is no real session at all, so a 401
      // only means the dev token can't reach this endpoint — never bounce
      // to /login there, let the caller render its own error state.
      clearTokens();
      window.location.href = "/login";
    }
    // Static token got 401 — return response as-is (old behavior)
  }

  return response;
}
